import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {Subscription, SubscriptionStatus} from "../../models/Subscription";
import {SubscriptionService} from "../../services/subscription.service";
import {ANNUAL_PRICE_MULTIPLIER} from "../../constants/subscription.constant";
import {HttpErrorResponse} from "@angular/common/http";
import {Observable} from "rxjs";

@Component({
  selector: 'app-manage-subscription',
  templateUrl: './manage-subscription.component.html',
  styleUrls: ['./manage-subscription.component.css']
})
export class ManageSubscriptionComponent implements OnInit {
  private subscriptionId: string | null = null;
  protected isEditMode: boolean = false;
  protected manageForm: FormGroup;
  protected loading = false;
  protected submitting = false;
  protected calculatedAnnualPrice: number = 0;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly subscriptionService: SubscriptionService
  ) {
    this.route.paramMap.subscribe((params: ParamMap): void => {
      this.subscriptionId = params.get('id');
      this.isEditMode = this.subscriptionId != null;
    });
    this.manageForm = this.createForm();
  }

  ngOnInit(): void {
    if (this.isEditMode && this.subscriptionId) {
      this.loadSubscription(this.subscriptionId);
    }

    this.manageForm.get('monthlyPrice')?.valueChanges.subscribe(this.updateCalculatedAnnualPrice.bind(this));
    this.manageForm.get('discountedAnnualRate')?.valueChanges.subscribe(this.updateCalculatedAnnualPrice.bind(this));
  }

  loadSubscription(id: string): void {
    this.loading = true;
    this.subscriptionService.getSubscriptionById(id).subscribe({
      next: (subscription: Subscription) => {
        if (subscription) {
          this.manageForm.patchValue({
            id: subscription.id,
            subscriptionName: subscription.subscriptionName,
            description: subscription.description,
            monthlyPrice: subscription.monthlyPrice,
            discountedAnnualRate: subscription.discountedAnnualRate,
            status: subscription.status,
            features: subscription.features
          });
          this.updateCalculatedAnnualPrice();
        }
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        throw new Error('Error loading subscription', error.error);
      }
    });
  }

  get getNameByPageMode(): string {
    return this.isEditMode ? 'Edit' : 'Create';
  }

  private createForm(): FormGroup {
    return this.fb.group({
      id: [''],
      subscriptionName: ['', {
        validators: [Validators.required],
        updateOn: 'blur'
      }],
      description: ['', [Validators.required, Validators.maxLength(100)]],
      monthlyPrice: [0, [Validators.required, Validators.min(0.01), this.validateDecimalPlaces(2)]],
      discountedAnnualRate: [null, [this.validateDiscountedRate(), this.validateDecimalPlaces(2)]],
      status: [SubscriptionStatus.DRAFT],
      features: this.fb.group({
        aiIntegration: [false],
        maxQuizRespondents: [100, [Validators.required, Validators.min(1), Validators.pattern('^[0-9]*$')]],
        maxQuestionsPerQuiz: [20, [Validators.required, Validators.min(1), Validators.pattern('^[0-9]*$')]],
        questionsOrder: this.fb.group({
          randomOrder: [false],
          allowSkipping: [false]
        }),
        testDuration: this.fb.group({
          enabled: [false],
          durationInMinutes: [null, [Validators.min(1), Validators.pattern('^[0-9]*$')]]
        }),
        honestRespondentTech: this.fb.group({
          enabled: [false],
          blockOnDetection: [false]
        }),
        aiAnswerReview: [false]
      })
    });
  }

  private validateDiscountedRate() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const monthlyPrice = control.parent?.get('monthlyPrice')?.value || 0;
      const discountedRate = control.value;

      if (discountedRate > monthlyPrice) {
        return {discountedRateTooHigh: true};
      }

      return null;
    };
  }

  private validateDecimalPlaces(maxDecimalPlaces: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const value = control.value.toString();
      const parts = value.split('.');

      if (parts.length > 1 && parts[1].length > maxDecimalPlaces) {
        return {invalidDecimalPlaces: true};
      }

      return null;
    };
  }

  updateCalculatedAnnualPrice(): void {
    const monthlyPrice = this.manageForm.get('monthlyPrice')?.value || 0;
    const discountedRate = this.manageForm.get('discountedAnnualRate')?.value || monthlyPrice;
    this.calculatedAnnualPrice = discountedRate * ANNUAL_PRICE_MULTIPLIER;
  }

  saveAsDraft(): void {
    this.save(SubscriptionStatus.DRAFT);
  }

  saveAndPublish(): void {
    this.save(SubscriptionStatus.PUBLISHED);
  }

  private save(status: SubscriptionStatus): void {
    if (this.manageForm.invalid) {
      this.markFormGroupTouched(this.manageForm);
      return;
    }

    this.submitting = true;
    const formData: Subscription = this.manageForm.value;
    formData.status = status;

    const operation: Observable<void> = this.isEditMode
      ? this.subscriptionService.updateSubscription(formData)
      : this.subscriptionService.createSubscription(formData);

    operation.subscribe({
      next: (): void => {
        this.submitting = false;
        this.router.navigate(['/subscriptions']).then();
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        throw new Error('Error saving subscription', error.error);
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  getFormControlError(controlName: string): string {
    const control = this.manageForm.get(controlName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    if (control.errors['required']) {
      return 'This field is required';
    }

    if (control.errors['maxlength']) {
      return `Maximum length is ${control.errors['maxlength'].requiredLength} characters`;
    }

    if (control.errors['min']) {
      return `Value must be at least ${control.errors['min'].min}`;
    }

    if (control.errors['pattern']) {
      return 'Please enter a valid number';
    }

    if (control.errors['notUnique']) {
      return 'Subscription name must be unique';
    }

    if (control.errors['discountedRateTooHigh']) {
      return 'Please enter a discounted annual rate that is less than or equal to the monthly price';
    }

    if (control.errors['invalidDecimalPlaces']) {
      return 'Please enter a value with up to 2 decimal places';
    }

    return 'Invalid value';
  }

  getNestedFormControlError(groupName: string, controlName: string): string {
    return this.getFormControlError(`${groupName}.${controlName}`);
  }

  isTestDurationRequired(): boolean {
    return this.manageForm.get('features.testDuration.enabled')?.value === true;
  }

  isHonestRespondentTechEnabled(): boolean {
    return this.manageForm.get('features.honestRespondentTech.enabled')?.value === true;
  }
}
